/** Локальное применение манипуляций ВПР «Ручное управление» (как на сервере). */

export const MANUAL_PIPELINE_ACTION_LABELS = {
  replace_text: 'Замена текста',
  phone_format: 'Преобразование телефонов',
  split_column: 'Разделение столбца',
  extract_phone: 'Извлечь телефон (ФИО + номер)',
  fallback_if_empty: 'Заполнить из столбца, если пусто',
  merge_sheets: 'Объединить все вкладки в один лист',
};

export const MANUAL_PIPELINE_ACTIONS = new Set([
  'replace_text',
  'phone_format',
  'split_column',
  'extract_phone',
  'fallback_if_empty',
  'merge_sheets',
]);

function parseConditionNumber(str) {
  if (str == null || str === '') return NaN;
  const t = String(str).trim().replace(/\s/g, '').replace(/,/g, '.');
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : NaN;
}

export function rowMatchesManipulationConditions(row, conditions) {
  if (!conditions || !Array.isArray(conditions) || conditions.length === 0) return true;
  for (const c of conditions) {
    const ci = parseInt(c.colIndex, 10);
    if (Number.isNaN(ci) || ci < 0) return false;
    const cell = row[ci] != null ? String(row[ci]).trim() : '';
    const op = c.op || 'equals';
    const val = c.value != null ? String(c.value) : '';
    switch (op) {
      case 'empty':
        if (cell !== '') return false;
        break;
      case 'not_empty':
        if (cell === '') return false;
        break;
      case 'equals':
        if (cell !== val.trim()) return false;
        break;
      case 'not_equals':
        if (cell === val.trim()) return false;
        break;
      case 'contains':
        if (!cell.includes(val)) return false;
        break;
      case 'not_contains':
        if (cell.includes(val)) return false;
        break;
      case 'num_gt':
      case 'num_gte':
      case 'num_lt':
      case 'num_lte':
      case 'num_eq':
      case 'num_neq': {
        const numCell = parseConditionNumber(cell);
        const numVal = parseConditionNumber(val);
        if (!Number.isFinite(numCell) || !Number.isFinite(numVal)) return false;
        let ok = false;
        if (op === 'num_gt') ok = numCell > numVal;
        else if (op === 'num_gte') ok = numCell >= numVal;
        else if (op === 'num_lt') ok = numCell < numVal;
        else if (op === 'num_lte') ok = numCell <= numVal;
        else if (op === 'num_eq') ok = numCell === numVal;
        else if (op === 'num_neq') ok = numCell !== numVal;
        if (!ok) return false;
        break;
      }
      default:
        break;
    }
  }
  return true;
}

const PHONE_HEADER_HINTS = [
  'телефон',
  'тел.',
  'тел ',
  'phone',
  'моб',
  'mobile',
  'номер тел',
  'телефон отрим',
  'телефон получ',
  'телефон клієнт',
];

/** Заголовки, которые не считаем телефоном (даже если есть цифры). */
const PHONE_HEADER_EXCLUDE = [
  'внутрішн',
  'внутрен',
  'накладн',
  'ттн',
  'ttn',
  'дата',
  'час',
  'планов',
  'статус',
  'адрес',
  'адреса',
  'контакт отрим',
  'контакт получ',
  'отримувач',
  'получат',
  'фіо',
  'фio',
  'піб',
];

const UA_MOBILE_OPERATOR_CODES = new Set([
  '39', '50', '63', '66', '67', '68', '73', '91', '92', '93', '94', '95', '96', '97', '98', '99',
]);

function headerLooksLikePhoneColumn(headerText) {
  const n = String(headerText || '').trim().toLowerCase();
  if (!n) return false;
  const hasPhoneHint = PHONE_HEADER_HINTS.some((hint) => n.includes(hint));
  if (!hasPhoneHint) return false;
  if (PHONE_HEADER_EXCLUDE.some((ex) => n.includes(ex)) && !n.includes('телефон') && !n.includes('phone')) {
    return false;
  }
  if ((n === 'номер' || n.startsWith('номер ')) && !n.includes('тел')) return false;
  return true;
}

/** Столбцы, в заголовке которых есть «телефон» / phone (не ТТН, не дата, не «внутрішній номер»). */
export function detectPhoneColumnIndices(headers) {
  const out = [];
  (headers || []).forEach((h, i) => {
    if (headerLooksLikePhoneColumn(h)) out.push(i);
  });
  return out;
}

/** Код оператора UA (9 цифр без ведущего 0). */
function uaMobileOperatorCode(nineDigits) {
  if (!nineDigits || nineDigits.length !== 9) return '';
  return nineDigits.slice(0, 2);
}

/** Не трогать даты, ТТН, номера накладных (204…), внутренние номера и текст с буквами. */
export function cellLooksLikeUkrainianPhone(val) {
  if (val == null || val === '') return false;
  const s = String(val).trim();
  if (/\d{1,2}[.\-/]\d{1,2}[.\-/]\d{2,4}(\s+\d{1,2}:\d{2})?/.test(s)) return false;
  if (/\d{1,2}:\d{2}/.test(s) && /\d{1,2}[.\-/]/.test(s)) return false;
  const withoutPlus = s.replace(/^\+/, '');
  if (/[a-zA-Zа-яА-ЯіїєґІЇЄҐ]/.test(withoutPlus)) return false;
  let digits = s.replace(/\D/g, '');
  if (digits.length < 9 || digits.length > 12) return false;
  if (digits.length >= 11 && digits.startsWith('380')) digits = digits.slice(2);
  else if (digits.length >= 10 && digits.startsWith('38')) digits = digits.slice(2);
  else if (digits.length >= 10 && (digits[0] === '8' || digits[0] === '7')) digits = digits.slice(1);
  if (digits.length === 10 && digits[0] === '0') digits = digits.slice(1);
  if (digits.length !== 9) return false;
  const op = uaMobileOperatorCode(digits);
  return UA_MOBILE_OPERATOR_CODES.has(op);
}

/**
 * UI: какие столбцы отмечены галочкой.
 * — массив (в т.ч. пустой) = ровно он;
 * — поле не задано = все столбцы (первое открытие).
 */
export function manualColIndicesSelectionSet(headers, indices) {
  const hdrLen = (headers || []).length;
  if (Array.isArray(indices)) {
    return new Set(
      indices
        .map((ci) => (typeof ci === 'number' ? ci : parseInt(ci, 10)))
        .filter((ci) => Number.isFinite(ci) && ci >= 0 && ci < hdrLen),
    );
  }
  return new Set(Array.from({ length: hdrLen }, (_, i) => i));
}

/** UI: выбор вкладок для merge_sheets (пустой массив = ни одной). */
export function manualSheetKeysSelectionSet(allKeys, selectedKeys) {
  if (Array.isArray(selectedKeys)) {
    return new Set(selectedKeys.map((k) => String(k)));
  }
  return new Set((allKeys || []).map((k) => String(k)));
}

/** Столбцы для «Замена текста» (явный выбор или все столбцы листа). */
export function resolveReplaceTextColumnIndices(headers, params) {
  const explicit = params?.replaceTextColIndices;
  if (Array.isArray(explicit)) {
    return explicit
      .map((ci) => (typeof ci === 'number' ? ci : parseInt(ci, 10)))
      .filter((ci) => Number.isFinite(ci) && ci >= 0 && ci < (headers || []).length);
  }
  return (headers || []).map((_, i) => i);
}

/** Поиск столбцов для фильтра в превью результата. */
export function detectManualResultFilterColumns(headers) {
  const hdrs = (headers || []).map((h) => String(h || '').trim().toLowerCase());
  let nameIdx = -1;
  let phoneIdx = -1;
  let waybillIdx = -1;
  hdrs.forEach((n, i) => {
    if (phoneIdx < 0 && (n.includes('телефон') || n.includes('phone') || n.includes('моб'))) phoneIdx = i;
    if (nameIdx < 0 && (n.includes('фіо') || n.includes('фio') || n.includes('контакт') || n.includes('отримувач') || n.includes('получат') || n.includes('клієнт') || n.includes('клиент'))) {
      if (!n.includes('телефон') && !n.includes('phone')) nameIdx = i;
    }
    if (waybillIdx < 0 && (n.includes('накладн') || n.includes('ттн') || n.includes('ttn') || n.includes('внутрішн') || n.includes('внутрен'))) {
      waybillIdx = i;
    }
    if (waybillIdx < 0 && (n === 'номер' || (n.startsWith('номер ') && !n.includes('тел')))) waybillIdx = i;
  });
  return { nameIdx, phoneIdx, waybillIdx };
}

export function manualResultRowPassesTextFilters(row, headers, filters) {
  const f = filters || {};
  const cols = detectManualResultFilterColumns(headers);
  const matchCol = (colIdx, query) => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return true;
    if (colIdx < 0) {
      return (row || []).some((cell) => String(cell ?? '').toLowerCase().includes(q));
    }
    return String(row[colIdx] ?? '').toLowerCase().includes(q);
  };
  return matchCol(cols.nameIdx, f.name) && matchCol(cols.phoneIdx, f.phone) && matchCol(cols.waybillIdx, f.waybill);
}

export function resolvePhoneFormatColumnIndices(headers, params) {
  const explicit = params?.phoneFormatColIndices;
  if (Array.isArray(explicit) && explicit.length > 0) {
    return explicit
      .map((ci) => (typeof ci === 'number' ? ci : parseInt(ci, 10)))
      .filter((ci) => Number.isFinite(ci) && ci >= 0 && ci < (headers || []).length);
  }
  return detectPhoneColumnIndices(headers);
}

export function normalizePhoneFormat(val, format) {
  if (val == null || val === '') return '';
  if (!cellLooksLikeUkrainianPhone(val)) return String(val);
  let s = String(val).replace(/\s+/g, '').replace(/[-()]/g, '');
  const digits = s.replace(/\D/g, '');
  if (digits.length < 9) return String(val);
  let code = digits.length >= 10 && (digits[0] === '8' || digits[0] === '7') ? digits.slice(1) : digits;
  if (code.length >= 10 && (code.startsWith('38') || code.startsWith('0'))) {
    code = code.replace(/^38/, '').replace(/^0/, '');
  }
  if (code.length < 9) return String(val);
  const a = code.slice(-9);
  const rest = a.replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  if (format === 'digits') return `0${a}`;
  if (format === 'intl') return `+38 0${rest}`;
  if (format === 'brackets') return `+380 (${a.slice(0, 2)}) ${a.slice(2, 5)}-${a.slice(5, 7)}-${a.slice(7, 9)}`;
  return `0${rest}`;
}

export function extractPhoneAndFio(str) {
  if (str == null || typeof str !== 'string') return { fio: '', phone: '' };
  const s = str.trim();
  if (!s) return { fio: '', phone: '' };
  const digitBlock = /[\d\s\-()+]{7,}/g;
  let best = null;
  let match;
  while ((match = digitBlock.exec(s)) !== null) {
    const chunk = match[0];
    const digits = chunk.replace(/\D/g, '');
    const len = digits.length;
    if (len >= 9 && len <= 12) {
      let code = digits;
      if (len >= 10 && (code.startsWith('8') || code.startsWith('7'))) code = code.slice(1);
      if (code.length >= 10 && (code.startsWith('38') || code.startsWith('0'))) {
        code = code.replace(/^38/, '').replace(/^0/, '');
      }
      if (code.length >= 9 && code.length <= 10) {
        best = { index: match.index, length: match[0].length, phone: chunk.trim() };
        break;
      }
    }
  }
  if (!best) return { fio: s, phone: '' };
  const before = s.slice(0, best.index).trim();
  const after = s.slice(best.index + best.length).trim();
  const fio = [before, after].filter(Boolean).join(' ').trim();
  return { fio, phone: best.phone };
}

export const MERGE_COL_SOURCE_TAB = 'Вкладка (источник)';
export const MERGE_COL_DATE_FROM_TAB = 'Дата из вкладки';
export const MERGED_SHEET_NAME = 'Объединённый';

const DATE_HEADER_HINTS = ['плановий час', 'дата', 'доставк', 'час достав', 'delivery', 'date', 'час'];

function normalizeMergeHeaderKey(h) {
  return String(h || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function cloneManualSheetsByName(sheetsByName) {
  if (!sheetsByName) return {};
  return Object.fromEntries(
    Object.entries(sheetsByName).map(([k, sh]) => [
      k,
      {
        ...sh,
        headers: (sh.headers || []).slice(),
        rows: (sh.rows || []).map((r) => (Array.isArray(r) ? r.slice() : [])),
      },
    ]),
  );
}

/** Сколько вкладок можно объединить (учитывает сохранённые оригиналы после merge). */
export function getMergeSourceSheets(entry) {
  if (!entry) return { names: [], byName: {} };
  const byName = entry._sourceSheetsByName || entry.sheetsByName || {};
  let names = (entry._sourceSheetNames && entry._sourceSheetNames.length > 0)
    ? [...entry._sourceSheetNames]
    : [...(entry.sheetNames || Object.keys(byName))];
  names = names.filter((n) => n && n !== MERGED_SHEET_NAME && byName[n]);
  if (names.length < 2) {
    names = Object.keys(byName).filter((n) => n !== MERGED_SHEET_NAME && byName[n]);
  }
  return { names, byName };
}

/** Список вкладок для UI (имя, строк, дата из ячеек). */
export function listMergeSheetOptions(entry) {
  const { names, byName } = getMergeSourceSheets(entry);
  return names.map((key) => {
    const sh = byName[key] || { headers: [], rows: [] };
    const label = sheetSourceTabLabel(sh, key);
    const rows = sh.rows || [];
    return {
      key,
      label,
      rowCount: rows.length,
      dateLabel: inferDateLabelFromSheetRows(rows, sh.headers, label),
    };
  });
}

export function resolveMergeSheetNames(entry, includeKeys) {
  const { names } = getMergeSourceSheets(entry);
  if (!Array.isArray(includeKeys) || includeKeys.length === 0) return names;
  const set = new Set(includeKeys);
  const picked = names.filter((n) => set.has(n));
  return picked.length > 0 ? picked : names;
}

/** Сводка по датам в столбце «Плановий час…» / «Дата из вкладки». */
export function summarizeRowsByDateColumn(rows, headers) {
  let ci = detectManualDateColIndex(headers);
  if (ci < 0) {
    ci = (headers || []).findIndex((h) => normalizeMergeHeaderKey(h) === normalizeMergeHeaderKey(MERGE_COL_DATE_FROM_TAB));
  }
  if (ci < 0) return [];
  const counts = new Map();
  for (const row of rows || []) {
    const raw = row[ci];
    if (raw == null || String(raw).trim() === '') continue;
    const day = String(raw).trim().split(/\s+/)[0];
    if (day) counts.set(day, (counts.get(day) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date, 'uk'));
}

/** Для превью: по несколько строк с каждой даты, чтобы были видны и 14-е, и 15-е. */
export function sampleRowsForMergedPreview(rows, headers, maxTotal = 50) {
  const list = rows || [];
  if (list.length <= maxTotal) return list;
  const groups = summarizeRowsByDateColumn(list, headers);
  if (groups.length <= 1) return list.slice(0, maxTotal);
  const ci = detectManualDateColIndex(headers);
  const dateCi = ci >= 0 ? ci : (headers || []).findIndex((h) => normalizeMergeHeaderKey(h) === normalizeMergeHeaderKey(MERGE_COL_DATE_FROM_TAB));
  if (dateCi < 0) return list.slice(0, maxTotal);
  const byDate = new Map();
  for (const row of list) {
    const day = String(row[dateCi] || '').trim().split(/\s+/)[0] || '(без даты)';
    if (!byDate.has(day)) byDate.set(day, []);
    byDate.get(day).push(row);
  }
  const perGroup = Math.max(1, Math.floor(maxTotal / byDate.size));
  const out = [];
  for (const [, gRows] of byDate) {
    out.push(...gRows.slice(0, perGroup));
  }
  return out.length > maxTotal ? out.slice(0, maxTotal) : out;
}

export function detectManualDateColIndex(headers) {
  const hdrs = headers || [];
  for (let i = 0; i < hdrs.length; i++) {
    const h = normalizeMergeHeaderKey(hdrs[i]);
    if (!h) continue;
    if (DATE_HEADER_HINTS.some((hint) => h.includes(hint))) return i;
  }
  return -1;
}

/** Дата для столбца «Дата из вкладки»: из ячеек листа (плановий час…), иначе из имени вкладки. */
export function inferDateLabelFromSheetRows(rows, headers, sheetName) {
  const fromName = guessDateFromSheetName(sheetName);
  const ci = detectManualDateColIndex(headers);
  if (ci < 0) return fromName;
  const counts = new Map();
  for (const row of rows || []) {
    const v = row[ci];
    if (v == null || String(v).trim() === '') continue;
    const day = String(v).trim().split(/\s+/)[0];
    if (day) counts.set(day, (counts.get(day) || 0) + 1);
  }
  if (counts.size === 0) return fromName;
  let best = fromName;
  let max = 0;
  for (const [d, c] of counts) {
    if (c > max) {
      max = c;
      best = d;
    }
  }
  return best;
}

function sheetSourceTabLabel(sh, sheetKey) {
  const orig = sh?.originalSheetName;
  if (orig != null && String(orig).trim() !== '') return String(orig).trim();
  return sheetKey;
}

/** Дата из имени вкладки (14.05, 14052025 …) → YYYY-MM-DD для поля date. */
export function guessDateFromSheetName(sheetName) {
  const s = String(sheetName || '').trim();
  let m = s.match(/(\d{1,2})[.\-/](\d{1,2})(?:[.\-/](\d{2,4}))?/);
  if (m) {
    let y = m[3] ? parseInt(m[3], 10) : new Date().getFullYear();
    if (y < 100) y += 2000;
    return `${y}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
  }
  m = s.match(/^(\d{2})(\d{2})(\d{2,4})?$/);
  if (m) {
    let y = m[3] ? parseInt(m[3], 10) : new Date().getFullYear();
    if (y < 100) y += 2000;
    return `${y}-${m[2]}-${m[1]}`;
  }
  return '';
}

function buildUnifiedHeadersForMerge(sheetNames, sheetsByName) {
  const unified = [];
  const seen = new Set();
  const addHeader = (h) => {
    const label = String(h || '').trim();
    const k = normalizeMergeHeaderKey(label);
    if (!k || seen.has(k)) return;
    seen.add(k);
    unified.push(label);
  };
  (sheetsByName[sheetNames[0]]?.headers || []).forEach(addHeader);
  for (const sn of sheetNames) {
    (sheetsByName[sn]?.headers || []).forEach(addHeader);
  }
  addHeader(MERGE_COL_SOURCE_TAB);
  addHeader(MERGE_COL_DATE_FROM_TAB);
  return unified;
}

function mapSheetRowToUnifiedHeaders(row, shHeaders, unifiedHeaders, sourceTabLabel, dateFromTabLabel) {
  const byKey = new Map();
  (shHeaders || []).forEach((h, ci) => {
    const k = normalizeMergeHeaderKey(h);
    if (k) byKey.set(k, row[ci] != null ? String(row[ci]) : '');
  });
  return unifiedHeaders.map((h) => {
    const k = normalizeMergeHeaderKey(h);
    if (k === normalizeMergeHeaderKey(MERGE_COL_SOURCE_TAB)) return sourceTabLabel;
    if (k === normalizeMergeHeaderKey(MERGE_COL_DATE_FROM_TAB)) return dateFromTabLabel;
    return byKey.get(k) ?? '';
  });
}

/** Объединить выбранные вкладки книги в один лист (столбцы по названию, не по номеру). */
export function mergeManualWorkbookSheets(entry, includeSheetKeys) {
  if (!entry?.sheetsByName && !entry?._sourceSheetsByName) return { entry, mergeStats: [] };

  const { names: allNames, byName: sourceByName } = getMergeSourceSheets(entry);
  const names = resolveMergeSheetNames(entry, includeSheetKeys);
  if (names.length < 2) {
    return {
      entry,
      mergeStats: [],
      error: names.length === 0
        ? 'Отметьте минимум 2 вкладки для объединения.'
        : `Отмечена только 1 вкладка («${names[0] || ''}»). Нужно минимум 2.`,
    };
  }

  if (!entry._sourceSheetsByName && allNames.length >= 2) {
    entry._sourceSheetsByName = cloneManualSheetsByName(entry.sheetsByName);
    entry._sourceSheetNames = [...allNames];
  }

  const headers = buildUnifiedHeadersForMerge(names, sourceByName);
  const rows = [];
  const mergeStats = [];

  for (const sn of names) {
    const sh = sourceByName[sn] || { headers: [], rows: [] };
    const shHeaders = sh.headers || [];
    const sheetRows = sh.rows || [];
    const sourceLabel = sheetSourceTabLabel(sh, sn);
    const dateLabel = inferDateLabelFromSheetRows(sheetRows, shHeaders, sourceLabel);
    mergeStats.push({ sheet: sourceLabel, rows: sheetRows.length, dateFromTab: dateLabel });
    for (const row of sheetRows) {
      rows.push(mapSheetRowToUnifiedHeaders(row, shHeaders, headers, sourceLabel, dateLabel));
    }
  }

  const sheetsByName = { [MERGED_SHEET_NAME]: { headers, rows } };
  const nextEntry = {
    ...entry,
    sheetNames: [MERGED_SHEET_NAME],
    sheetsByName,
    excelSheetName: MERGED_SHEET_NAME,
    headers,
    rows,
    workbookSourceSlot: undefined,
    _sourceSheetsByName: entry._sourceSheetsByName || cloneManualSheetsByName(sourceByName),
    _sourceSheetNames: entry._sourceSheetNames || [...names],
  };
  return { entry: nextEntry, mergeStats, totalRows: rows.length };
}

/**
 * Применить одно действие к данным слота (мутирует sheet: headers, rows, split-meta).
 * @returns {{ ok: boolean, error?: string, selectedCols?: number[] }}
 */
export function applyManualFileActionToSheet(sheet, fileConfig, selectedCols) {
  if (!sheet || !fileConfig) return { ok: false, error: 'Нет данных' };
  const action = fileConfig.action || '';
  if (!MANUAL_PIPELINE_ACTIONS.has(action)) {
    return { ok: false, error: 'Это действие настраивается отдельно (расхождения / сравнение по телефону)' };
  }

  const params = fileConfig.params || {};
  const rows = sheet.rows || [];
  const headers = sheet.headers || [];

  if (action === 'replace_text') {
    const find = (params.replaceFind != null ? String(params.replaceFind) : '').trim();
    if (!find) {
      return { ok: false, error: 'Укажите текст для поиска (поле «Найти текст»).' };
    }
    const repl = params.replaceWith != null ? String(params.replaceWith) : '';
    const conds = params.manipulationConditions;
    const colsToUse = resolveReplaceTextColumnIndices(headers, params);
    if (colsToUse.length === 0) {
      return { ok: false, error: 'Отметьте хотя бы один столбец для замены (или «Все столбцы»).' };
    }
    colsToUse.forEach((ci) => {
      rows.forEach((row) => {
        if (!rowMatchesManipulationConditions(row, conds)) return;
        if (ci >= 0) {
          row[ci] = String(row[ci] ?? '').split(find).join(repl);
        }
      });
    });
    return { ok: true, selectedCols: (selectedCols || []).slice(), replaceTextColIndices: colsToUse };
  }

  if (action === 'phone_format') {
    const fmt = fileConfig.method || 'ua';
    const conds = params.manipulationConditions;
    const colsToUse = resolvePhoneFormatColumnIndices(headers, params);
    if (colsToUse.length === 0) {
      return {
        ok: false,
        error: 'Отметьте один или несколько столбцов с телефонами (ниже) или нажмите «Найти по заголовку».',
      };
    }
    colsToUse.forEach((ci) => {
      rows.forEach((row) => {
        if (!rowMatchesManipulationConditions(row, conds)) return;
        if (ci >= 0 && row[ci] != null) row[ci] = normalizePhoneFormat(row[ci], fmt);
      });
    });
    return { ok: true, selectedCols: (selectedCols || []).slice(), phoneFormatColIndices: colsToUse };
  }

  if (action === 'fallback_if_empty') {
    const targetIdx = Math.max(0, parseInt(params.fallbackTargetColIndex, 10) || 0);
    const sourceIdx = Math.max(0, parseInt(params.fallbackSourceColIndex, 10) || 0);
    if (targetIdx >= headers.length || sourceIdx >= headers.length) {
      return { ok: false, error: 'Неверные столбцы для заполнения' };
    }
    const conds = params.manipulationConditions;
    const maxIdx = Math.max(targetIdx, sourceIdx, headers.length - 1);
    rows.forEach((row) => {
      if (!rowMatchesManipulationConditions(row, conds)) return;
      while (row.length <= maxIdx) row.push('');
      const cur = row[targetIdx] != null ? String(row[targetIdx]).trim() : '';
      if (cur === '') row[targetIdx] = row[sourceIdx] != null ? String(row[sourceIdx]) : '';
    });
    return { ok: true, selectedCols: (selectedCols || []).slice() };
  }

  if (action === 'split_column') {
    const colIdx = Math.max(0, parseInt(params.splitColIndex, 10) || 0);
    if (colIdx >= headers.length) return { ok: false, error: 'Столбец для разделения не найден' };
    const delim = (params.splitDelimiter != null ? String(params.splitDelimiter) : ',').trim() || ',';
    const newCol1 = `${headers[colIdx] || `Кол.${colIdx + 1}`}_часть1`;
    const newCol2 = `${headers[colIdx] || `Кол.${colIdx + 1}`}_часть2`;
    sheet.headers = headers.slice(0, colIdx).concat([newCol1, newCol2]).concat(headers.slice(colIdx + 1));
    sheet._splitColOriginalIndex = colIdx;
    sheet._splitColIndices = [colIdx, colIdx + 1];
    rows.forEach((row) => {
      const val = row[colIdx] != null ? String(row[colIdx]) : '';
      const i = val.indexOf(delim);
      const part1 = i >= 0 ? val.slice(0, i).trim() : val;
      const part2 = i >= 0 ? val.slice(i + delim.length).trim() : '';
      const before = row.slice(0, colIdx);
      const after = row.slice(colIdx + 1);
      row.length = 0;
      row.push(...before, part1, part2, ...after);
    });
    const newSelected = remapSelectedColsAfterSplit(selectedCols, colIdx);
    return { ok: true, selectedCols: newSelected };
  }

  if (action === 'extract_phone') {
    const colIdx = Math.max(0, parseInt(params.extractPhoneColIndex, 10) || 0);
    if (colIdx >= headers.length) return { ok: false, error: 'Столбец для извлечения не найден' };
    const fmt = params.extractPhoneFormat || fileConfig.method || 'ua';
    const baseName = headers[colIdx] || `Кол.${colIdx + 1}`;
    const newCol1 = `${baseName}_ФИО`;
    const newCol2 = `${baseName}_Телефон`;
    sheet.headers = headers.slice(0, colIdx).concat([newCol1, newCol2]).concat(headers.slice(colIdx + 1));
    sheet._splitColOriginalIndex = colIdx;
    sheet._splitColIndices = [colIdx, colIdx + 1];
    rows.forEach((row) => {
      const val = row[colIdx] != null ? String(row[colIdx]).trim() : '';
      const { fio, phone } = extractPhoneAndFio(val);
      const phoneFormatted = phone ? normalizePhoneFormat(phone, fmt) : '';
      const before = row.slice(0, colIdx);
      const after = row.slice(colIdx + 1);
      row.length = 0;
      row.push(...before, fio, phoneFormatted, ...after);
    });
    const newSelected = remapSelectedColsAfterSplit(selectedCols, colIdx);
    return { ok: true, selectedCols: newSelected };
  }

  return { ok: false, error: 'Неизвестное действие' };
}

function remapSelectedColsAfterSplit(selectedCols, colIdx) {
  const effective = (selectedCols || []).filter((c) => c !== colIdx);
  const out = [];
  for (const c of effective) {
    if (c < colIdx) out.push(c);
    else if (c > colIdx) out.push(c + 1);
  }
  out.push(colIdx, colIdx + 1);
  return [...new Set(out)].sort((a, b) => a - b);
}

export function serializeManualActionStep(fileConfig) {
  const c = fileConfig || {};
  return {
    action: c.action,
    method: c.method,
    params: JSON.parse(JSON.stringify(c.params || {})),
  };
}

/** Перед «Запустить»: подставить актуальные поля формы в очередь (последний шаг того же действия или единственный шаг). */
export function syncManualActionPipelineForRun(fileConfig, selectedCols) {
  const c = fileConfig || {};
  const action = c.action || '';
  if (!MANUAL_PIPELINE_ACTIONS.has(action)) return c;
  const pipeline = Array.isArray(c.actionPipeline) ? c.actionPipeline.map((s) => ({
    ...s,
    params: { ...(s.params || {}) },
    selectedCols: Array.isArray(s.selectedCols) ? s.selectedCols.slice() : [],
  })) : [];
  const liveStep = {
    ...serializeManualActionStep(c),
    selectedCols: Array.isArray(selectedCols) ? selectedCols.slice() : [],
  };
  if (pipeline.length === 0) {
    return { ...c, actionPipeline: [liveStep] };
  }
  const last = pipeline[pipeline.length - 1];
  if (last && last.action === action) {
    pipeline[pipeline.length - 1] = liveStep;
  } else {
    pipeline.push(liveStep);
  }
  return { ...c, actionPipeline: pipeline };
}

export function getManualActionStepLabel(step) {
  return MANUAL_PIPELINE_ACTION_LABELS[step?.action] || step?.action || 'Действие';
}

/** Применить действие к entry (файл в слоте): merge_sheets обрабатывает всю книгу. */
export function applyManualActionToEntry(entry, fileConfig, selectedCols) {
  if (!entry) return { ok: false, error: 'Сначала загрузите файл в слот' };
  const action = fileConfig?.action || '';
  if (action === 'merge_sheets') {
    const { names: available } = getMergeSourceSheets(entry);
    if (available.length < 2) {
      const already = entry.excelSheetName === MERGED_SHEET_NAME || (entry.sheetNames || []).includes(MERGED_SHEET_NAME);
      return {
        ok: false,
        error: already
          ? 'Данные уже объединены. Чтобы заново — ✕ у слота и загрузите файл.'
          : 'В файле меньше 2 вкладок. Проверьте вкладки внизу Excel (Sheet1, Лист1…).',
      };
    }
    const include = fileConfig.params?.mergeSheetsInclude;
    const { entry: mergedEntry, mergeStats, totalRows, error: mergeErr } = mergeManualWorkbookSheets(entry, include);
    if (mergeErr) return { ok: false, error: mergeErr };
    Object.assign(entry, mergedEntry);
    const cols = (mergedEntry.headers || []).map((_, i) => i);
    const dateSummary = summarizeRowsByDateColumn(mergedEntry.rows, mergedEntry.headers);
    return { ok: true, selectedCols: cols, mergeStats, totalRows, dateSummary };
  }
  const sheet = {
    headers: entry.headers || [],
    rows: entry.rows || [],
    _splitColOriginalIndex: entry._splitColOriginalIndex,
    _splitColIndices: entry._splitColIndices,
  };
  const res = applyManualFileActionToSheet(sheet, fileConfig, selectedCols);
  if (!res.ok) return res;
  entry.headers = sheet.headers;
  entry.rows = sheet.rows;
  entry._splitColOriginalIndex = sheet._splitColOriginalIndex;
  entry._splitColIndices = sheet._splitColIndices;
  return res;
}
